import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { usePatchProviderLicenseCollabAgreementMutation } from '@uplift/queries';
import { EditCollabAgreementForm, EditCollabAgreementFormValues } from '../forms/EditCollabAgreementForm';
import { License, LicenseCollaborativeAgreement } from '@uplift/api';
import { useAlert } from '../../AlertProvider';
import { getDefaultDate } from '../utils';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';

const editCollabAgreementValidationSchema = Yup.object().shape({
  noExpiration: Yup.boolean(),
  expirationDate: Yup.string().when('noExpiration', {
    is: true,
    then: schema => schema.notRequired(),
    otherwise: schema => schema.required('Expiration date is required'),
  }),
  physicianFirstName: Yup.string().required('Physician first name is required'),
  physicianLastName: Yup.string().required('Physician last name is required'),
  physicianNpi: Yup.string().length(10, 'NPI must be 10 digits').required('NPI is required'),
  physicianEmail: Yup.string().email('Invalid email address').required('Email is required'),
  physicianPhone: Yup.string().nullable(),
});

export type EditCollabAgreementModalProps = {
  onClose: () => void;
  onCancel?: () => void;
  license: Pick<License, 'id' | 'userId' | 'stateId'>;
  agreement: LicenseCollaborativeAgreement;
};

export const EditCollabAgreementModal: React.VFC<EditCollabAgreementModalProps> = ({ ...props }) => {
  const alert = useAlert();
  const patchMutation = usePatchProviderLicenseCollabAgreementMutation(props.license.userId);

  const onSubmit = async (values: EditCollabAgreementFormValues) => {
    await patchMutation.mutateAsync({
      collabAgreementId: props.agreement.id,
      data: {
        ...values,
        expirationDate: values.noExpiration ? null : getDefaultDate(values.expirationDate),
      }
    });

    alert.success('Collaborative agreement uploaded');
    props.onClose();
  };

  const initialValues: EditCollabAgreementFormValues = {
    expirationDate: props.agreement.expirationDate ? CalendarDateService.date(props.agreement.expirationDate, 'YYYY-MM-DD').format(DateFormats.DEFAULT_DATE) : '',
    noExpiration: !props.agreement.expirationDate,
    physicianFirstName: props.agreement.physicianFirstName ?? '',
    physicianLastName: props.agreement.physicianLastName ?? '',
    physicianNpi: props.agreement.physicianNpi ?? '',
    physicianEmail: props.agreement.physicianEmail ?? '',
    physicianPhone: props.agreement.physicianPhone ?? '',
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={editCollabAgreementValidationSchema}
      onSubmit={onSubmit}
    >
      {formik => <EditCollabAgreementForm props={props} formik={formik} />}
    </Formik>
  );
};
